import { firstValueFrom, from, of, throwError as observableThrowError } from "rxjs";

import { catchError, map } from "rxjs/operators";
import { Injectable } from "@angular/core";

import { HttpClient } from "../../shared/services/http/http-client.service";
import { TripCharge } from "@app/shared/models/trips/TripCharge.model";
import { TripOptions } from "@app/shared/models/trips/TripOptions.model";
import { InsuranceSurchargeViewModel } from "@app/shared/models/rates/InsuranceSurchargeViewModel.model";
import { LongevityViewModel } from "@app/shared/models/rates/LongevityViewModel.model";
import { FuelSurchargeViewModel } from "@app/shared/models/rates/FuelSurchargeViewModel.model";
import { CustomChargeViewModel } from "@app/shared/models/rates/CustomChargeViewModel.model";
import { DriverRateViewModel } from "@app/shared/models/rates/DriverRateViewModel.model";
import { MinimumRateViewModel } from "@app/shared/models/rates/MinimumRateViewModel.model";
import { ClientRateViewModel } from "@app/shared/models/rates/ClientRateViewModel.model";
import { ClientLaneRateViewModel } from "@app/shared/models/rates/ClientLaneRateViewModel.model";
import { TripData } from "@app/shared/models/trips/TripData.model";

/** An attached trip file (may be a temp file with a softDelete flag). */
export interface TripAttachedFile {
  id?: string;
  tempId?: string | number;
  softDelete?: boolean;
  fileName?: string;
  originalFileName?: string;
  originalName?: string;
  name?: string;
  blobName?: string;
  type?: string;
  clientEmailSentUtc?: string | Date | null;
  uploadedBy?: string | null;
}

@Injectable()
export class NewTripService {
  filteredTrips: any[] = [];
  selectedfiles: TripAttachedFile[] = [];
  constructor(private _http: HttpClient) {}

  getCharges(tripId: string | number) {
    return this._http.get<TripCharge[]>(`api/trip/getCharges/${tripId}`).pipe(
      map((res: TripCharge[]) => {
        return res;
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server trip creation error")
      )
    );
  }

  getNextTripNumber() {
    return this._http
      .get("api/trip/getNextTripNo")
      .pipe(
        map((res: any) => {
          return res as any;
        }),
        catchError((error: any) =>
          observableThrowError(error.message || "Server trip creation error")
        )
      )
      .toPromise();
  }

  checkTripNumberExists(tripNumber: string, tripId: string) {
    return this._http
      .get(`api/trip/checkTripNumberExists/?tripNumber=${tripNumber}&tripId=${tripId}`)
      .pipe(
        map((res: any) => {
          return res as any;
        }),
        catchError((error: any) =>
          observableThrowError(error.message || "Server trip creation error")
        )
      )
      .toPromise();
  }

  searchTrips(searchText: string) {
    return this._http.get(`api/trip/searchTrips/?searchText=${searchText}`).pipe(
      map((res: any) => {
        return res as any;
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server trip creation error")
      )
    );
  }

  updateChargesAsPromise(
    tripId: string | number,
    tripCharges: TripCharge[],
    driverCharges: TripCharge[]
  ) {
    if (!tripCharges && !driverCharges) return;
    return this._http
      .patch(`api/trip/updateCharges/${tripId}`, {
        tripCharges: this.normalizeCharges(tripCharges),
        driverCharges: this.normalizeCharges(driverCharges),
      })
      .toPromise();
  }

  getNextTripInFilteredList(currentID: string) {
    const stored = localStorage.getItem("tripListStatus");
    const tripListStatus = stored ? JSON.parse(stored) : null;
    if (!tripListStatus) return false;
    const trips = tripListStatus.tripsIdList;
    if (!trips) return false;
    for (let i = 0; i < trips.length; i++) {
      if (trips[i] === currentID) {
        // if is the last one??
        if (i === trips.length - 1) {
          return false;
        } else {
          return trips[++i];
        }
      }
    }
    return false;
  }

  getTripList(offset: number, limit: number, filter: unknown, order: unknown) {
    return this._http.post("api/trip/list", { offset, limit, filter, order }).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getCancelledTripList(offset: number, limit: number, filters: unknown, order: unknown) {
    return this._http
      .post("api/trip/listCancelledTrips", {
        offset,
        limit,
        filter: filters,
        order,
      })
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error: any) =>
          observableThrowError(error.message || "Server Trip Cancelled List error")
        )
      );
  }

  getQueueTripList(filter = {}, order = {}) {
    return this._http.post("api/trip/listqueue", { filter, order }).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getNewListQueueTrips() {
    return this._http.post("api/trip/listQueueTrip", {}).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getListFilterQueue() {
    return this._http.post("api/trip/listFilterQueue", {}).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getFilterBillingTrips() {
    return this._http.post("api/trip/listFilterBilling", {}).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }
  getNewListBillingTrips() {
    return this._http.post("api/trip/listBillingTrip", {}).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getClientList() {
    return this._http.post("api/client/list", { validOnly: true, includeDeleted: true }).pipe(
      map((res: any) => {
        let client = res;
        client.map(
          (client: { companyName: string; itemName?: string }) =>
            (client.itemName = client.companyName)
        );
        return client;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getListFilterBilling() {
    return this._http.post("api/trip/listFilterBilling", {}).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getNewListBilledTrips() {
    return this._http.post("api/trip/listBilledTrip", {}).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getListFilterBilled(
    filter: any,
    pageSize: number,
    pageOffset: number,
    sortField: string,
    sortOrder: number
  ) {
    return this._http
      .post("api/trip/newListBilledFilterView", {
        filter,
        pageSize,
        pageOffset,
        sortField,
        sortOrder,
      })
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error: any) => observableThrowError(error.message || "Server error"))
      );
  }

  getListPaginatedQueue(
    filter: any,
    pageSize: number,
    pageOffset: number,
    sortField: string,
    sortOrder: number
  ) {
    return this._http
      .post("api/trip/newListQueuePaginatedView", {
        filter,
        pageSize,
        pageOffset,
        sortField,
        sortOrder,
      })
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error: any) => observableThrowError(error.message || "Server error"))
      );
  }

  mobileCommsList(
    filter: any,
    pageSize: number,
    pageOffset: number,
    sortField: string,
    sortOrder: number,
    view: "active" | "deleted" = "active"
  ) {
    const normalizedFilter = (filter?.fileName || "").toString().trim();
    const optionalFilters: Record<string, any> = {
      source: filter?.source,
      uploadedBy: filter?.uploadedBy,
      clientEmailSent: filter?.clientEmailSent,
      createdFrom: filter?.dateCreatedUtc_from,
      createdTo: filter?.dateCreatedUtc_to,
      deletedFrom: filter?.dateDeletedUtc_from,
      deletedTo: filter?.dateDeletedUtc_to,
    };
    const activeOptionalFilters = Object.fromEntries(
      Object.entries(optionalFilters)
        .map(([key, value]) => [key, (value ?? "").toString().trim()])
        .filter(([, value]) => value !== "")
    );
    const query = new URLSearchParams({
      fileName: normalizedFilter,
      ...activeOptionalFilters,
      view,
      pageSize: String(pageSize ?? 25),
      pageOffset: String(pageOffset ?? 0),
      sortField: sortField || "fileName",
      sortOrder: String(sortOrder ?? 1),
    }).toString();

    return this._http.get(`api/mobilecomms/list?${query}`).pipe(
      map((res: any) => {
        const records = Array.isArray(res?.records) ? res.records : [];
        const totalRecords = Number(res?.totalRecords ?? records.length);
        const activeTotalRecords = Number(res?.activeTotalRecords ?? totalRecords);
        return {
          records: records.map((record: any) => ({
            ...record,
            fileName: record.fileName ?? record.originalName ?? record.name,
            uploadedBy: record.uploadedBy ?? "-",
            isDeleted: !!record.dateDeletedUtc,
            clientEmailSent: record.clientEmailSentUtc ? "Yes" : "No",
          })),
          totalRecords,
          activeTotalRecords,
          ids: records.map((record: any) => record.id),
        };
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  uploadMobileCommsPdf(file: File) {
    const formData = new FormData();
    formData.append("trip", file, file.name);
    return this._http.upload("/api/upload", formData).pipe(
      map((response: any) => ({
        id: response?.id || response?.name,
        fileName: response?.originalName || response?.name,
        originalName: response?.originalName || response?.name,
        name: response?.name,
        uploadedBy: response?.uploadedBy ?? null,
      }))
    );
  }

  renameMobileCommsFile(fileId: string, fileName: string): any {
    const normalizedName = (fileName || "").trim();
    if (!fileId || !normalizedName) {
      return of(null as any);
    }

    return this._http.patch("api/mobilecomms/rename", { id: fileId, fileName: normalizedName }).pipe(
      map((updatedFile: any) => ({
        id: updatedFile?.id,
        fileName: updatedFile?.fileName,
      }))
    );
  }

  deleteMobileCommsPdf(fileId: string): any {
    if (!fileId) {
      return of(null as any);
    }

    return this._http.delete(`api/mobilecomms/${fileId}`).pipe(
      map((result: any) => result ?? { deleted: true })
    );
  }

  restoreMobileCommsPdf(fileId: string): any {
    if (!fileId) {
      return of(null as any);
    }

    return this._http.patch(`api/mobilecomms/restore/${fileId}`, {}).pipe(
      map((result: any) => result ?? { restored: true })
    );
  }

  attachMobileCommsFileToTrip(file: TripAttachedFile, tripId: string) {
    const normalizedTripId = (tripId || "").trim();
    if (!file?.id || !normalizedTripId) {
      return of(null as any);
    }

    return this._http.patch("api/trip/updateAttachedFiles", {
      tripId: normalizedTripId,
      attachedFiles: [
        {
          id: file.id,
          originalName: file.originalName || file.fileName || file.name || "",
          name: file.name || file.blobName || file.originalName || file.fileName || "",
          blobName: file.blobName || file.name || file.originalName || file.fileName || "",
          type: file.type || "application/pdf",
          clientEmailSentUtc: file.clientEmailSentUtc ?? null,
          softDelete: false,
        },
      ],
    }).pipe(
      map((result: any) => result ?? [])
    );
  }

  sendMobileCommsEmail(
    fileId: string,
    to: string,
    subject: string,
    body: string,
    ccEmails?: string,
    bccEmails?: string
  ) {
    if (!fileId || !to || !subject) {
      return of(null as any);
    }

    const formData = new FormData();
    formData.append("id", fileId);
    formData.append("to", to);
    formData.append("subject", subject);
    formData.append("body", body || "");
    if (ccEmails) formData.append("ccEmails", ccEmails);
    if (bccEmails) formData.append("bccEmails", bccEmails);

    return this._http.post("api/mobilecomms/send-internal", formData, null, true, true).pipe(
      map((result: any) => result ?? { sent: true, deleted: true })
    );
  }

  sendMobileCommsClientEmail(fileId: string, to: string, subject: string, body: string, ccEmails?: string, attachmentFileName?: string) {
    if (!fileId || !to || !subject) {
      return of(null as any);
    }

    const formData = new FormData();
    formData.append("id", fileId);
    formData.append("to", to);
    formData.append("subject", subject);
    formData.append("body", body || "");
    if (ccEmails) formData.append("ccEmails", ccEmails);
    if (attachmentFileName) formData.append("attachmentFileName", attachmentFileName);

    return this._http.post("api/mobilecomms/send-client-email", formData, null, true, true).pipe(
      map((result: any) => result ?? { sent: true, sentUtc: new Date().toISOString() })
    );
  }

  syncDriveAxleScans() {
    return this._http.post("api/mobilecomms/sync-driveaxle", {}).pipe(
      map((result: any) => result ?? { message: "DriveAxle scan sync completed" })
    );
  }

  downloadDriveAxleScan(documentIdentifier: string) {
    const normalizedIdentifier = (documentIdentifier || "").trim();
    if (!normalizedIdentifier) {
      return of(null as any);
    }

    return this._http.post("api/mobilecomms/download-driveaxle-scan", { documentIdentifier: normalizedIdentifier }).pipe(
      map((result: any) => result ?? { alreadyImported: false, documentIdentifier: normalizedIdentifier })
    );
  }

  getMobileCommsPdf(fileName: string) {
    const encodedName = encodeURIComponent(fileName);
    return this._http.get<{ url: string }>(`api/download/trip/${encodedName}`).pipe(
      map((response: any) => ({
        fileName,
        url: response?.url || response?.Url || "",
      }))
    );
  }

  getMobileCommsPdfBlob(fileName: string) {
    return this.getMobileCommsPdf(fileName);
  }

  getMobileCommsPdfPreviewBlob(fileId: string) {
    if (!fileId) {
      return of({ blob: null } as any);
    }

    return from(this._http.download(`api/mobilecomms/preview/${encodeURIComponent(fileId)}`)).pipe(
      map((response: any) => ({
        blob: response?.body instanceof Blob ? response.body : null,
      }))
    );
  }

  getListPaginatedInvoice(
    filter: any,
    pageSize: number,
    pageOffset: number,
    sortField: string,
    sortOrder: number
  ) {
    return this._http
      .post("api/trip/newListInvoicePaginatedView", {
        filter,
        pageSize,
        pageOffset,
        sortField,
        sortOrder,
      })
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error: any) => observableThrowError(error.message || "Server error"))
      );
  }

  getListPaginatedBilling(
    filter: any,
    pageSize: number,
    pageOffset: number,
    sortField: string,
    sortOrder: number
  ) {
    return this._http
      .post("api/trip/newListBillingPaginatedView", {
        filter,
        pageSize,
        pageOffset,
        sortField,
        sortOrder,
      })
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error: any) => observableThrowError(error.message || "Server error"))
      );
  }

  getListPaginatedCancelled(
    filter: any,
    pageSize: number,
    pageOffset: number,
    sortField: string,
    sortOrder: number
  ) {
    return this._http
      .post("api/trip/newListCancelledPaginatedView", {
        filter,
        pageSize,
        pageOffset,
        sortField,
        sortOrder,
      })
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error: any) => observableThrowError(error.message || "Server error"))
      );
  }

  getExportTrips(tripsIds: string[], filter: any, selectAll: boolean, page: string) {
    return this._http
      .post("api/trip/exportTrips", {
        tripsIds,
        filter,
        selectAll,
        page,
      })
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error: any) => observableThrowError(error.message || "Server error"))
      );
  }

  getNewListCancelledTrips() {
    //VARGAS WTF UNA FUNCION POST QUE NO RECIVE PARAMETROS?
    return this._http.post("api/trip/listCancelledTrip", {}).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getListFilterCancelled() {
    //VARGAS WTF UNA FUNCION POST QUE NO RECIVE PARAMETROS?
    return this._http.post("api/trip/listFilterCancelled", {}).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  newListBilled() {
    return this._http.get("api/trip/newListBilledView").pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server trip listing error"))
    );
  }

  getListReadyForBillView() {
    return this._http.post("api/trip/listReadyForBillView", {}).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server trip listing error"))
    );
  }

  newGetListReadyForBillView() {
    return this._http.get("api/trip/newListReadyForBillView").pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server trip listing error"))
    );
  }

  getBillingTripList(offset: number, limit: number, filter: unknown, order: unknown) {
    return this._http.post("api/trip/listbilling", { offset, limit, filter, order }).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getBilledTripList(offset: number, limit: number, filter: unknown, order: unknown) {
    return this._http.post("api/trip/listbilled", { offset, limit, filter, order }).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getInvoiceTripList(offset: number, limit: number, filter: unknown, order: unknown) {
    return this._http.post("api/trip/listinvoice", { offset, limit, filter, order }).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getTripInfo(id: string) {
    return this._http.get("api/trip/find/" + id).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server home Trip info List error")
      )
    );
  }

  findOverlappingTripsAsync(
    startDate: Date,
    endDate: Date,
    driverId: string,
    trailerId: string,
    tractorId: string
  ): Promise<any> {
    return firstValueFrom(
      this._http.post("api/trip/findOverlapping", {
        startDate,
        endDate,
        driverId,
        trailerId,
        tractorId,
      })
    );
  }

  async isTripBackupValid(id: string): Promise<any> {
    return firstValueFrom(this._http.get("api/trip/isBackupValid/" + id));
  }

  createNewTrip(newTrip: TripData, tripBilling: any, payments: any[]) {
    const payload = this.normalizeTripPayload(newTrip, tripBilling, payments);
    return this._http
      .post("api/trip/createWeb", {
        trip: payload.trip,
        billing: payload.billing,
        payments: payload.payments,
        driverBillToMismatch: newTrip.driverBillToMismatch || false,
        driverAssignedBillToName: newTrip.driverAssignedBillToName || null,
      })
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error: any) =>
          observableThrowError(error.message || "Server trip creation error")
        )
      );
  }

  createNewTripUpload(files: File[]) {
    return this._http.post("api/trip/createFromPicturesWeb", { Files: files }).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server trip creation error")
      )
    );
  }

  getClientsDropDown() {
    return this._http.get("api/client/listDropDown").pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server trip creation error")
      )
    );
  }
  //Goodbye schedules
  /*  getSchedulebyClientDate(clientId, startDate, endDate) {
      const client = {
        ClientId: clientId,
        startDate: startDate,
        endDate: endDate,
      };
      return this._http
        .post("api/schedule/listByClientDate", { schedule: client }, null, false)
        .pipe(
          map((res) => {
            return res;
          }),
          catchError((error: any) =>
            observableThrowError(error.message || "Server trip creation error")
          )
        );
    }*/

  getDriversDropDown() {
    return this._http.get("api/driver/listDropDown").pipe(
      map((res: any) => {
        let drivers = res;
        drivers.map(
          (driver: { name: string; lastName: string; itemName?: string }) =>
            (driver.itemName = driver.lastName + " " + driver.name)
        );
        return drivers;
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server trip creation error")
      )
    );
  }

  listDriversTripDropDown() {
    return this._http.get("api/driver/listTripDropDown").pipe(
      map((res: any) => {
        let drivers = res;
        drivers.map(
          (driver: { name: string; lastName: string; itemName?: string }) =>
            (driver.itemName = driver.lastName + " " + driver.name)
        );
        return drivers;
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server trip creation error")
      )
    );
  }

  getTractorsDropDown() {
    return this._http.get("api/tractor/listDropDown").pipe(
      map((res: any) => {
        let tractors = res;
        tractors.map(
          (tractor: { unitNumber: string; itemName?: string }) =>
            (tractor.itemName = tractor.unitNumber)
        );
        return tractors;
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server trip creation error")
      )
    );
  }

  getTeamsDropDown() {
    return this._http.get("api/team/listDropDown").pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server trip creation error")
      )
    );
  }

  getProductsDropDown() {
    return this._http.get("api/product/listDropDown").pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server trip creation error")
      )
    );
  }

  getTrailersDropDown() {
    return this._http.get("api/trailer/listDropDown").pipe(
      map((res: any) => {
        let trailers = res;
        trailers = trailers.filter(
          (trailer: { trailerNo: string | null }) => trailer.trailerNo !== null
        );
        return trailers;
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server trip creation error")
      )
    );
  }

  listShippers() {
    return this._http.get("api/shipper/listAll").pipe(
      map((res: any) => {
        let shippers = res;
        shippers.map(
          (shipper: { locId: string; itemName?: string }) => (shipper.itemName = shipper.locId)
        );
        return shippers;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getLocationsDropDown(clientId: string) {
    return this._http.post("api/location/listByClientDropDown", { clientId }).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server trip creation error")
      )
    );
  }

  getBillAdressesDropDown(clientId: string) {
    const client = {
      clientId: clientId,
    };
    return this._http.post("api/billAddress/listDropDown", { billAddress: client }).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server trip creation error")
      )
    );
  }

  getFuelPriceByStateAndDate(state: string, fuelDate: string | Date) {
    const filter = {
      state: state,
      fuelDate: fuelDate,
    };
    return this._http.post("api/gas/getFuelPriceByState", { filter }).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server trip creation error")
      )
    );
  }

  async getFuelPriceByStateAndDateAsync(state: string, fuelDate: Date): Promise<any> {
    const filter = {
      state: state,
      fuelDate: fuelDate,
    };
    return firstValueFrom(this._http.post("api/gas/getFuelPriceByState", { filter }));
  }

  async getFuelPriceByRegionAndDateAsync(
    region: string,
    fuelDate: Date,
    effectiveDate: number
  ): Promise<number> {
    return firstValueFrom(
      this._http.post("api/gas/getFuelPriceByRegion", { region, fuelDate, effectiveDate })
    );
  }

  async getClientRatesFromOptions(tripOptions: TripOptions): Promise<ClientRateViewModel[]> {
    const { tripData, ...options } = tripOptions;
    return firstValueFrom(this._http.post("api/rates/getClientRatesFromOptions", { options }));
  }

  async getClientLaneRatesFromOptions(
    tripOptions: TripOptions
  ): Promise<ClientLaneRateViewModel[]> {
    const { tripData, ...options } = tripOptions;
    return firstValueFrom(this._http.post("api/rates/getClientLaneRatesFromOptions", { options }));
  }

  async getDriverLaneRatesFromOptions(tripOptions: TripOptions): Promise<DriverRateViewModel[]> {
    const { tripData, ...options } = tripOptions;
    return firstValueFrom(this._http.post("api/rates/getDriverLaneRatesFromOptions", { options }));
  }

  async getDriverGuaranteedRate(
    driverId: string | number,
    date: Date
  ): Promise<MinimumRateViewModel> {
    return firstValueFrom(
      this._http.post("api/rates/minimums/getDriverGuarantee", { driverId, date })
    );
  }

  async getClientGuaranteedRate(
    clientId: string | number,
    date: Date
  ): Promise<MinimumRateViewModel> {
    return firstValueFrom(
      this._http.post("api/rates/minimums/getClientGuarantee", { clientId, date })
    );
  }

  async getDriverRatesFromOptions(tripOptions: TripOptions): Promise<DriverRateViewModel[]> {
    const { tripData, ...options } = tripOptions;
    return firstValueFrom(this._http.post("api/rates/getDriverRatesFromOptions", { options }));
  }

  async getInsuranceRatesFromOptions(
    tripOptions: TripOptions
  ): Promise<InsuranceSurchargeViewModel[]> {
    const { tripData, ...options } = tripOptions;
    return firstValueFrom(this._http.post("api/rates/getInsuranceRatesFromOptions", { options }));
  }

  async getLongevityFromOptions(tripOptions: TripOptions): Promise<LongevityViewModel[]> {
    const { tripData, ...options } = tripOptions;
    return firstValueFrom(this._http.post("api/rates/getLongevityFromOptions", { options }));
  }

  async getFuelRatesFromOptions(
    tripOptions: TripOptions,
    ratingType: string
  ): Promise<FuelSurchargeViewModel[]> {
    const { tripData, ...options } = tripOptions;
    return firstValueFrom(
      this._http.post("api/rates/getFuelRatesFromOptions", { options: { ...options, ratingType } })
    );
  }

  async getFuelSurchargeRegionForState(
    fuelSurchargeId: string | number,
    state: string
  ): Promise<any> {
    return firstValueFrom(
      this._http.get(`api/rates/fuelSurcharges/${fuelSurchargeId}/getRegionForState?state=${state}`)
    );
  }

  async getDriverFuelSurchargeRegionForState(
    driverFuelSurchargeId: string | number,
    state: string
  ): Promise<any> {
    return firstValueFrom(
      this._http.get(
        `api/rates/driverFuelSurcharges/${driverFuelSurchargeId}/getRegionForState?state=${state}`
      )
    );
  }

  async getCustomChargesFromOptions(
    tripOptions: TripOptions,
    type: string,
    driverId?: string
  ): Promise<CustomChargeViewModel[]> {
    const { tripData, ...options } = tripOptions;
    return firstValueFrom(
      this._http.post("api/rates/getCustomChargesFromOptions", {
        options,
        type,
        driverId,
      })
    );
  }

  getTripInfoByTripNo(tripNo: string) {
    return this._http.get("api/trip/findByTripNo/" + tripNo).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server home Trip info List error")
      )
    );
  }

  updateTrip(trip: TripData, billing: any, payments: any[]) {
    return this._http
      .patch("api/trip/update", this.normalizeTripPayload(trip, billing, payments))
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error: any) => observableThrowError(error.message || "Server error"))
      );
  }

  updateTripAsPromise(trip: TripData, billing: any, payments: any[]) {
    return firstValueFrom(
      this._http.patch("api/trip/update", this.normalizeTripPayload(trip, billing, payments))
    );
  }

  updateTripAttachedFilesAsPromise(tripId: string, attachedFiles: TripAttachedFile[]) {
    return this._http
      .patch("api/trip/updateAttachedFiles", { tripId, attachedFiles })
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error: any) => observableThrowError(error.message || "Server error"))
      )
      .toPromise();
  }

  /**
   * The charge tables use editable number inputs, so a manual edit can leave a string
   * (or an empty string) on a numeric charge field. The API binds `miles` to an int and
   * rejects values that are not whole numbers ("could not convert string to integer"),
   * which broke trip creation. Coerce the numeric charge fields on the outgoing payload
   * so a hand-edited or stale (localStorage restored) value can never break a save.
   */
  private normalizeCharge(charge: TripCharge): TripCharge {
    return {
      ...charge,
      miles: this.toInteger(charge.miles),
      units: this.toNumber(charge.units),
      chargeRate: this.toNumber(charge.chargeRate),
      price: this.toNumber(charge.price),
      calculatedMiles: this.toInteger(charge.calculatedMiles),
      calculatedUnits: this.toNumber(charge.calculatedUnits),
      calculatedChargeRate: this.toNumber(charge.calculatedChargeRate),
      calculatedPrice: this.toNumber(charge.calculatedPrice),
    };
  }

  private normalizeCharges(charges?: TripCharge[] | null): TripCharge[] | undefined {
    if (!charges) return charges ?? undefined;
    return charges.map((charge) => this.normalizeCharge(charge));
  }

  private normalizeTripPayload(trip: TripData, billing: any, payments: any[]) {
    return {
      trip: {
        ...trip,
        tripCharges: this.normalizeCharges(trip.tripCharges),
        driverCharges: this.normalizeCharges(trip.driverCharges),
        payrollCharges: this.normalizeCharges(trip.payrollCharges),
      },
      billing: billing
        ? {
            ...billing,
            tripCharges: this.normalizeCharges(billing.tripCharges),
            invoiceCharges: this.normalizeCharges(billing.invoiceCharges),
          }
        : billing,
      payments: (payments ?? []).map((payment) => ({
        ...payment,
        driverCharges: this.normalizeCharges(payment?.driverCharges),
      })),
    };
  }

  private toNumber(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private toInteger(value: unknown): number {
    return Math.round(this.toNumber(value));
  }

  addAttachedInvoicesAsPromise(tripId: string, files: any[]) {
    return this._http
      .patch("api/trip/addAttachedInvoices", { tripId, attachedFiles: files })
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error: any) => observableThrowError(error.message || "Server error"))
      )
      .toPromise();
  }

  deleteAttachedInvoice(id: string) {
    return this._http.post("api/trip/deleteAttachedInvoice/" + id, {}).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  deleteTrip(id: string) {
    return this._http.delete("api/trip/delete/" + id);
  }

  restoreTrip(id: string) {
    return this._http.post("api/trip/restore", { id });
  }

  sendToBilling(id: string) {
    return this._http.get("api/trip/sendToBillingReview/" + id);
  }

  sendBatchToBillingReview(tripsIDs: string[]) {
    return this._http.post("api/trip/sendBatchToBillingReview/", { tripsIDs }).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server trip creation error")
      )
    );
  }

  sendToBillingQueue(id: string) {
    return this._http.get("api/trip/sendToBillingQueue/" + id);
  }

  sendBatchToBillingQueue(tripsIDs: string[]) {
    return this._http.post("api/trip/sendBatchToBillingQueue/", { tripsIDs }).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server trip creation error")
      )
    );
  }

  sendBatchToReadyForBill(tripsIDs: string[]) {
    return this._http.post("api/trip/sendBatchToReadyForBill/", { tripsIDs }).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server trip creation error")
      )
    );
  }

  sendToReadyForBill(id: string) {
    return this._http.get("api/trip/sentToReadyForBill/" + id);
  }

  sendBackToBillingReview(id: string) {
    return this._http.get("api/trip/sendBackToBillingReview/" + id);
  }

  setTripListStatusData(view: { currentView?: string; tripsIdList?: string[] }) {
    const { currentView, tripsIdList } = view;
    const stored = localStorage.getItem("tripListStatus");
    const currentStatus = (stored ? JSON.parse(stored) : {}) || {};
    if (currentView) currentStatus.currentView = currentView;
    if (tripsIdList) currentStatus.tripsIdList = tripsIdList || [];
    localStorage.setItem("tripListStatus", JSON.stringify(currentStatus));
  }

  getTripListStatusData() {
    const stored = localStorage.getItem("tripListStatus");
    return stored ? JSON.parse(stored) : null;
  }

  getListBillingView() {
    return this._http.post("api/trip/listbillingView", {}).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server trip listing error"))
    );
  }

  printInvoices(tripsIDs: string[], includeBackup = true) {
    return this._http.post("api/trip/printInvoices", { tripsIDs, includeBackup }, "blob").pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server trip listing error"))
    );
  }

  printInvoicesFiltered(
    tripsIds: string[],
    includeBackup = true,
    filter: any,
    selectAll: boolean,
    page: string
  ) {
    return this._http
      .post(
        "api/trip/printInvoicesFiltered",
        {
          tripsIds,
          includeBackup,
          filter,
          selectAll,
          page,
        },
        "blob"
      )
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error: any) => observableThrowError(error.message || "Server error"))
      );
  }

  printAttachedInvoices(tripsIDs: string[]) {
    return this._http.post("api/trip/printAttachedInvoices", { tripsIDs }, "blob").pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server trip listing error"))
    );
  }

  printBackup(tripIDs: string) {
    return this._http.post("api/trip/printBackup", { tripIDs }, "blob").pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server trip listing error"))
    );
  }

  moveInvoicesToBilled(tripsIDs: string[]) {
    return this._http.post("api/trip/moveInvoicesToBilled", { tripsIDs }, "blob").pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server trip listing error"))
    );
  }

  runBillingCycle(tripsIDs: string[]) {
    return this._http.post("api/trip/runBillingCycle", { tripsIDs }).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server trip listing error"))
    );
  }

  approveTrips(tripIds: string[]) {
    return this._http.post("api/trip/approveTrips", { tripIds }).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server trip listing error"))
    );
  }

  getRuleName(ruleId: string) {
    return this._http.post("api/trip/getRuleName", { ruleId }).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server trip listing error"))
    );
  }

  getBillingEfficiencyReport(startDate: Date, endDate: Date) {
    return this._http
      .post("api/trip/billingEfficiency", {
        startDate: new Date(startDate),
        endDate: new Date(endDate),
      })
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error: any) =>
          observableThrowError(error.message || "Server trip listing error")
        )
      );
  }
}
